import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";

export default function SupportTickets({ tickets, onTicketsUpdate }) {
  const { t } = useTranslation();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [newTicketPriority, setNewTicketPriority] = useState('medium');
  const [ticketReply, setTicketReply] = useState('');

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`);
      setSelectedTicket(response.data.ticket);
      setTicketMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error(t('support.errorLoadingTicket'));
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) {
      toast.error(t('support.fillAllFields'));
      return;
    }

    try {
      await axios.post(`${API}/tickets`, {
        subject: newTicketSubject,
        message: newTicketMessage,
        priority: newTicketPriority
      });

      toast.success(t('support.ticketCreated'));
      setShowCreateTicket(false);
      setNewTicketSubject('');
      setNewTicketMessage('');
      setNewTicketPriority('medium');
      onTicketsUpdate();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(t('support.errorCreatingTicket'));
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!ticketReply.trim()) return;

    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/messages`, {
        message: ticketReply
      });

      setTicketReply('');
      fetchTicketDetails(selectedTicket.id);
      toast.success(t('support.messageSent'));
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(t('support.errorSendingMessage'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'open': <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{t('support.statusOpen')}</span>,
      'in_progress': <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{t('support.statusInProgress')}</span>,
      'closed': <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{t('support.statusClosed')}</span>
    };
    return badges[status] || status;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">{t('support.priorityLow')}</span>,
      'medium': <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">{t('support.priorityMedium')}</span>,
      'high': <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">{t('support.priorityHigh')}</span>
    };
    return badges[priority] || priority;
  };

  if (selectedTicket) {
    return (
      <Card className="border-2 border-black">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <Button
                onClick={() => setSelectedTicket(null)}
                variant="outline"
                className="mb-2"
              >
                ← {t('support.backToList')}
              </Button>
              <CardTitle className="text-xl mt-2">{selectedTicket.subject}</CardTitle>
              <CardDescription className="mt-2 flex gap-2">
                {getStatusBadge(selectedTicket.status)}
                {getPriorityBadge(selectedTicket.priority)}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Messages */}
          <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
            {ticketMessages.map((msg) => (
              <div
                key={msg.id}
                className={`p-4 rounded-lg ${
                  msg.author_type === 'admin'
                    ? 'bg-blue-50 border-l-4 border-blue-500'
                    : 'bg-gray-50 border-l-4 border-gray-300'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-semibold">
                    {msg.author_type === 'admin' ? `👨‍💼 ${t('support.supportTeam')}` : `👤 ${t('support.you')}`}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              </div>
            ))}
          </div>

          {/* Reply form */}
          {selectedTicket.status !== 'closed' && (
            <form onSubmit={handleSendReply} className="border-t pt-4">
              <Label htmlFor="reply">{t('support.reply')}</Label>
              <textarea
                id="reply"
                value={ticketReply}
                onChange={(e) => setTicketReply(e.target.value)}
                className="w-full mt-1 p-3 border-2 border-black rounded-md"
                rows="3"
                placeholder={t('support.enterYourMessage')}
              />
              <Button
                type="submit"
                className="mt-2 bg-black text-white"
                disabled={!ticketReply.trim()}
              >
                {t('support.send')}
              </Button>
            </form>
          )}
          {selectedTicket.status === 'closed' && (
            <div className="text-center p-4 bg-gray-100 rounded-lg">
              <p className="text-gray-600">{t('support.ticketClosed')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-black">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-xl">🎫 {t('support.supportTickets')}</CardTitle>
            <CardDescription>{t('support.askOurTeam')}</CardDescription>
          </div>
          <Button
            onClick={() => setShowCreateTicket(!showCreateTicket)}
            className="bg-black text-white"
          >
            {showCreateTicket ? t('support.cancel') : `+ ${t('support.createTicket')}`}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showCreateTicket && (
          <form onSubmit={handleCreateTicket} className="mb-6 p-4 border-2 border-black rounded-lg bg-gray-50">
            <h3 className="font-bold mb-3">{t('support.newTicket')}</h3>
            
            <div className="mb-3">
              <Label htmlFor="subject">{t('support.subject')}</Label>
              <Input
                id="subject"
                value={newTicketSubject}
                onChange={(e) => setNewTicketSubject(e.target.value)}
                placeholder={t('support.subjectPlaceholder')}
                className="border-2 border-black"
                required
              />
            </div>

            <div className="mb-3">
              <Label htmlFor="priority">{t('support.priority')}</Label>
              <select
                id="priority"
                value={newTicketPriority}
                onChange={(e) => setNewTicketPriority(e.target.value)}
                className="w-full p-2 border-2 border-black rounded-md"
              >
                <option value="low">{t('support.priorityLow')}</option>
                <option value="medium">{t('support.priorityMedium')}</option>
                <option value="high">{t('support.priorityHigh')}</option>
              </select>
            </div>

            <div className="mb-3">
              <Label htmlFor="message">{t('support.message')}</Label>
              <textarea
                id="message"
                value={newTicketMessage}
                onChange={(e) => setNewTicketMessage(e.target.value)}
                className="w-full p-3 border-2 border-black rounded-md"
                rows="4"
                placeholder={t('support.messagePlaceholder')}
                required
              />
            </div>

            <Button type="submit" className="bg-black text-white">
              {t('support.createTicket')}
            </Button>
          </form>
        )}

        {/* Tickets list */}
        {tickets.length === 0 && !showCreateTicket && (
          <div className="text-center py-8 text-gray-500">
            <p>{t('support.noTicketsYet')}</p>
            <p className="text-sm mt-2">{t('support.createFirstTicket')}</p>
          </div>
        )}

        {tickets.length > 0 && (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => fetchTicketDetails(ticket.id)}
                className="p-4 border-2 border-gray-300 rounded-lg hover:border-black cursor-pointer transition-all"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold">{ticket.subject}</h4>
                  <div className="flex gap-2">
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                  </div>
                </div>
                <div className="text-sm text-gray-600 flex justify-between">
                  <span>💬 {t('support.messagesCount')}: {ticket.message_count || 0}</span>
                  <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
